import algoliasearch, { type SearchIndex } from "algoliasearch/lite";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "@/config/firebase";

const functions = getFunctions(app, "us-central1");
const CREDENTIALS_FUNCTION_NAME = "getCollaboratorSearchCredentials";
const DEFAULT_LIMIT = 8;
const MAX_LIMIT = 20;
const HIGHLIGHT_PRE_TAG = "<em>";
const HIGHLIGHT_POST_TAG = "</em>";
const SEARCHABLE_ATTRIBUTES = ["name"] as const;
const SEARCH_FILTERS = "status:ACTIVE";
const SEARCH_ATTRIBUTES_TO_RETRIEVE = [
  "name",
  "email",
  "photoUrl",
  "positionName",
  "isRaroAcademyAlumnus",
  "status",
  "objectID",
  "objectId",
  "id",
] as const;
const CREDENTIAL_REFRESH_SKEW_MS = 30_000;

export interface HighlightRange {
  start: number;
  end: number;
}

export interface CollaboratorSearchResult {
  collaboratorId: string;
  name: string;
  avatarUrl: string | null;
  profilePath: string;
  status: string | null;
  email: string | null;
  positionName: string | null;
  isRaroAcademyAlumnus: boolean | null;
  highlights?: {
    name: HighlightRange[];
  };
}

interface AlgoliaSearchCredentialsResponse {
  appId: string;
  indexName: string;
  apiKey: string;
  expiresAt: number;
}

interface CachedAlgoliaCredentials extends AlgoliaSearchCredentialsResponse {
  userUid: string | null;
}

interface AlgoliaHighlightEntry {
  value: string;
  matchLevel: "none" | "partial" | "full";
  fullyHighlighted?: boolean;
  matchedWords: string[];
}

interface AlgoliaHit {
  objectID?: string;
  objectId?: string;
  id?: string;
  name?: string;
  email?: string;
  photoUrl?: string;
  positionName?: string;
  isRaroAcademyAlumnus?: boolean;
  status?: string;
  _highlightResult?: {
    name?: AlgoliaHighlightEntry;
  };
}

const getCredentials = httpsCallable<void, AlgoliaSearchCredentialsResponse>(
  functions,
  CREDENTIALS_FUNCTION_NAME
);

let cachedCredentials: CachedAlgoliaCredentials | null = null;
let cachedIndex: SearchIndex | null = null;
let credentialsPromise: Promise<SearchIndex> | null = null;

function getCurrentUserUid(): string | null {
  return getAuth(app).currentUser?.uid ?? null;
}

function parseLimit(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return DEFAULT_LIMIT;
  }

  const normalized = Math.max(1, Math.floor(value));
  return Math.min(normalized, MAX_LIMIT);
}

function hasValidCachedCredentials(userUid: string | null): boolean {
  if (!cachedCredentials || !cachedIndex) {
    return false;
  }

  if (cachedCredentials.userUid !== userUid) {
    return false;
  }

  return cachedCredentials.expiresAt * 1000 > Date.now() + CREDENTIAL_REFRESH_SKEW_MS;
}

function clearCachedCredentials(): void {
  cachedCredentials = null;
  cachedIndex = null;
}

async function getSearchIndex(): Promise<SearchIndex> {
  const userUid = getCurrentUserUid();

  if (hasValidCachedCredentials(userUid)) {
    return cachedIndex as SearchIndex;
  }

  if (credentialsPromise) {
    return credentialsPromise;
  }

  credentialsPromise = (async () => {
    const response = await getCredentials();
    const credentials = response.data;
    const nextUserUid = getCurrentUserUid();

    if (!credentials?.appId || !credentials.apiKey || !credentials.indexName || !credentials.expiresAt) {
      throw new Error("Credenciais Algolia inválidas.");
    }

    const client = algoliasearch(credentials.appId, credentials.apiKey);
    const index = client.initIndex(credentials.indexName);

    cachedCredentials = {
      ...credentials,
      userUid: nextUserUid,
    };
    cachedIndex = index;

    return index;
  })();

  try {
    return await credentialsPromise;
  } finally {
    credentialsPromise = null;
  }
}

function extractHighlightRanges(highlightValue: string): HighlightRange[] {
  const ranges: HighlightRange[] = [];
  let cursor = 0;
  let searchStart = 0;

  while (searchStart < highlightValue.length) {
    const startTagIndex = highlightValue.indexOf(HIGHLIGHT_PRE_TAG, searchStart);
    if (startTagIndex === -1) {
      break;
    }

    const matchStart = cursor + (startTagIndex - searchStart);
    const contentStart = startTagIndex + HIGHLIGHT_PRE_TAG.length;
    const endTagIndex = highlightValue.indexOf(HIGHLIGHT_POST_TAG, contentStart);

    if (endTagIndex === -1) {
      break;
    }

    const matchLength = endTagIndex - contentStart;
    ranges.push({
      start: matchStart,
      end: matchStart + matchLength,
    });

    cursor = matchStart + matchLength;
    searchStart = endTagIndex + HIGHLIGHT_POST_TAG.length;
  }

  return ranges;
}

function mapHitToResult(hit: AlgoliaHit): CollaboratorSearchResult | null {
  const collaboratorId =
    (typeof hit.objectID === "string" && hit.objectID) ||
    (typeof hit.objectId === "string" && hit.objectId) ||
    (typeof hit.id === "string" && hit.id) ||
    null;

  const name = typeof hit.name === "string" ? hit.name.trim() : "";

  if (!collaboratorId || !name) {
    return null;
  }

  const avatarUrl =
    typeof hit.photoUrl === "string" && hit.photoUrl.trim().length > 0
      ? hit.photoUrl
      : null;

  const status = typeof hit.status === "string" ? hit.status : null;
  const email = typeof hit.email === "string" ? hit.email : null;
  const positionName = typeof hit.positionName === "string" ? hit.positionName : null;
  const isRaroAcademyAlumnus =
    typeof hit.isRaroAcademyAlumnus === "boolean" ? hit.isRaroAcademyAlumnus : null;

  const highlightValue = hit._highlightResult?.name?.value;
  const nameHighlights = highlightValue ? extractHighlightRanges(highlightValue) : [];

  return {
    collaboratorId,
    name,
    avatarUrl,
    profilePath: `/collaborators/${collaboratorId}`,
    status,
    email,
    positionName,
    isRaroAcademyAlumnus,
    highlights: {
      name: nameHighlights,
    },
  };
}

async function runSearch(index: SearchIndex, query: string, limit: number): Promise<CollaboratorSearchResult[]> {
  const response = await index.search<AlgoliaHit>(query, {
    hitsPerPage: limit,
    attributesToRetrieve: [...SEARCH_ATTRIBUTES_TO_RETRIEVE],
    attributesToHighlight: [...SEARCHABLE_ATTRIBUTES],
    highlightPreTag: HIGHLIGHT_PRE_TAG,
    highlightPostTag: HIGHLIGHT_POST_TAG,
    restrictSearchableAttributes: [...SEARCHABLE_ATTRIBUTES],
    filters: SEARCH_FILTERS,
  });

  return (response.hits || [])
    .map((hit) => mapHitToResult(hit))
    .filter((entry): entry is CollaboratorSearchResult => Boolean(entry));
}

export async function searchCollaboratorsByName(
  query: string,
  limit?: number
): Promise<CollaboratorSearchResult[]> {
  const normalizedQuery = query.trim();

  if (normalizedQuery.length < 2) {
    return [];
  }

  const normalizedLimit = parseLimit(limit);

  try {
    const index = await getSearchIndex();
    return await runSearch(index, normalizedQuery, normalizedLimit);
  } catch (error) {
    clearCachedCredentials();

    const retryable =
      error instanceof Error &&
      /forbidden|unauthorized|expired|api key|permission/i.test(error.message);

    if (!retryable) {
      throw error;
    }

    const index = await getSearchIndex();
    return await runSearch(index, normalizedQuery, normalizedLimit);
  }
}
